// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/** Shell pieces: modals, the event drawer, glossary tooltips, toasts. */

import { GLOSSARY } from './glossary';

let modalHost: HTMLElement | null = null;
let drawerEl: HTMLElement | null = null;
let toastHost: HTMLElement | null = null;
let lastFocus: HTMLElement | null = null;

export function registerShell(opts: { modalHost: HTMLElement; drawer: HTMLElement; toastHost: HTMLElement }): void {
  modalHost = opts.modalHost;
  drawerEl = opts.drawer;
  toastHost = opts.toastHost;

  modalHost.addEventListener('click', (e) => {
    if (e.target === modalHost) closeModal();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (modalHost && !modalHost.hidden) {
      closeModal();
      return;
    }
    if (drawerEl && !drawerEl.hidden) closeDrawer();
  });
}

export function openModal(title: string, bodyHtml: string): void {
  if (!modalHost) return;
  lastFocus = document.activeElement as HTMLElement;
  modalHost.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="${escapeAttr(title)}">
      <div class="modal-head">
        <h2>${escapeHtml(title)}</h2>
        <button type="button" class="modal-close" aria-label="Close">&times;</button>
      </div>
      <div class="modal-body">${bodyHtml}</div>
    </div>
  `;
  modalHost.hidden = false;
  document.body.classList.add('locked');
  modalHost.querySelector<HTMLButtonElement>('.modal-close')?.addEventListener('click', closeModal);
  modalHost.querySelector<HTMLButtonElement>('.modal-close')?.focus();
  wireGlossary(modalHost);
}

export function closeModal(): void {
  if (!modalHost) return;
  modalHost.hidden = true;
  modalHost.innerHTML = '';
  document.body.classList.remove('locked');
  lastFocus?.focus();
}

export function isModalOpen(): boolean {
  return Boolean(modalHost && !modalHost.hidden);
}

export function openDrawer(): void {
  if (!drawerEl) return;
  drawerEl.hidden = false;
  drawerEl.classList.add('open');
}

export function closeDrawer(): void {
  if (!drawerEl) return;
  drawerEl.classList.remove('open');
  drawerEl.hidden = true;
}

export function toggleDrawer(): void {
  if (!drawerEl) return;
  if (drawerEl.hidden) openDrawer();
  else closeDrawer();
}

export function toast(message: string, kind: 'ok' | 'warn' | 'err' = 'ok'): void {
  if (!toastHost) return;
  const el = document.createElement('div');
  el.className = `toast ${kind}`;
  el.textContent = message;
  toastHost.appendChild(el);
  setTimeout(() => {
    el.classList.add('out');
    setTimeout(() => el.remove(), 300);
  }, 2600);
}

/* ── Glossary ──────────────────────────────────────────────────── */

let tooltip: HTMLElement | null = null;

export function glossaryTerm(term: string, label = term): string {
  if (!GLOSSARY[term]) return escapeHtml(label);
  return `<span class="glossary-link" data-term="${escapeAttr(term)}" tabindex="0" role="button">${escapeHtml(label)}</span>`;
}

export function wireGlossary(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('.glossary-link').forEach((el) => {
    if (el.dataset.wired) return;
    el.dataset.wired = '1';
    const show = (e: Event) => {
      e.stopPropagation();
      showTooltip(el);
    };
    el.addEventListener('click', show);
    el.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') show(e);
    });
  });
}

function showTooltip(el: HTMLElement): void {
  const term = el.dataset.term ?? '';
  const text = GLOSSARY[term];
  if (!text) return;

  hideTooltip();
  tooltip = document.createElement('div');
  tooltip.className = 'glossary-tip';
  tooltip.innerHTML = `<strong>${escapeHtml(term)}</strong><p>${escapeHtml(text)}</p>`;
  document.body.appendChild(tooltip);

  const r = el.getBoundingClientRect();
  const tw = Math.min(340, window.innerWidth - 24);
  tooltip.style.width = `${tw}px`;
  const left = Math.max(12, Math.min(window.innerWidth - tw - 12, r.left));
  const below = r.bottom + 8;
  const fitsBelow = below + tooltip.offsetHeight < window.innerHeight - 12;
  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${fitsBelow ? below : Math.max(12, r.top - tooltip.offsetHeight - 8)}px`;

  setTimeout(() => document.addEventListener('click', hideTooltip, { once: true }), 0);
}

function hideTooltip(): void {
  tooltip?.remove();
  tooltip = null;
}

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') hideTooltip();
});

/* ── Escaping ──────────────────────────────────────────────────── */

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

export function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/* ── Small building blocks ─────────────────────────────────────── */

export function icon(name: string): string {
  // Lucide-style 24×24 stroked paths, inlined so nothing is fetched.
  const paths: Record<string, string> = {
    file: '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v5h6"/>',
    layers: '<path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 17 9 5 9-5"/>',
    scissors: '<circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4 8.12 15.88"/><path d="M14.47 14.48 20 20"/><path d="M8.12 8.12 12 12"/>',
    'git-compare': '<circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M13 6h3a2 2 0 0 1 2 2v7"/><path d="M11 18H8a2 2 0 0 1-2-2V9"/>',
    database: '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/><path d="M3 12a9 3 0 0 0 18 0"/>',
    shield: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1Z"/>',
    info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
    terminal: '<path d="m4 17 6-6-6-6"/><path d="M12 19h8"/>',
    download: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/><path d="M12 15V3"/>',
    copy: '<rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
    x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
    check: '<path d="M20 6 9 17l-5-5"/>',
    alert: '<path d="M12 9v4"/><path d="M12 17h.01"/><circle cx="12" cy="12" r="10"/>',
  };
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[name] ?? ''}</svg>`;
}
